import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AddToBag } from "@/components/AddToBag";
import { Eyebrow, MaskText, Reveal } from "@/components/motion";
import { ShadeFinder } from "@/components/ShadeFinder";
import { SHADES, SHADE_PRICE, collectionUrl, discountPct, formatINR, productUrl } from "@/data/catalog";
import { useLiveProduct } from "@/lib/live";
import { EASE } from "@/lib/scroll";

const orbBg = (colors) =>
  `radial-gradient(circle at var(--lx,32%) var(--ly,28%), #fffaf0 0%, ${colors[0]} 16%, ${colors[1]} 58%, #120d0a 100%)`;

const Orb = ({ shade }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ref.current.style.setProperty("--lx", `${((e.clientX - r.left) / r.width) * 100}%`);
    ref.current.style.setProperty("--ly", `${((e.clientY - r.top) / r.height) * 100}%`);
  };
  return (
    <div ref={ref} onMouseMove={onMove} data-testid="shade-lab-orb" className="relative h-40 w-40 shrink-0 sm:h-48 sm:w-48">
      <AnimatePresence initial={false}>
        <motion.div
          key={shade.id}
          initial={{ opacity: 0, scale: 0.85, rotate: -20 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          exit={{ opacity: 0, scale: 1.08 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="orb absolute inset-0 rounded-full"
          style={{ background: orbBg(shade.colors) }}
        />
      </AnimatePresence>
      <div className="orb-glitter pointer-events-none absolute inset-0 rounded-full" />
    </div>
  );
};

export const ShadeLab = () => {
  const [idx, setIdx] = useState(0);
  const s = SHADES[idx];
  const live = useLiveProduct(SHADES[idx]);
  const price = { price: live.live ? live.price : SHADE_PRICE.price, compare: live.live ? live.compare : SHADE_PRICE.compare };
  const off = discountPct(price.price, price.compare);

  useEffect(() => {
    SHADES.forEach((sh) => {
      const img = new Image();
      img.src = sh.look;
    });
  }, []);

  return (
    <section id="shade-lab" data-testid="shade-lab-section" className="relative overflow-hidden bg-ink-2 py-24 lg:py-36">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <div className="relative aspect-[4/5] overflow-hidden lg:col-span-6 lg:aspect-auto lg:min-h-[720px]" data-testid="shade-lab-look">
          <AnimatePresence initial={false}>
            <motion.img
              key={s.id}
              src={s.look}
              alt={`Celestial ${s.name} worn`}
              initial={{ clipPath: "inset(100% 0% 0% 0%)", scale: 1.15 }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
              exit={{ opacity: 1, transition: { duration: 1.1 } }}
              transition={{ duration: 1.1, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
            />
          </AnimatePresence>
          <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_at_30%_20%,transparent_40%,rgba(10,9,8,0.55)_100%)]" />
          <div className="absolute bottom-4 left-4 z-20 h-24 w-24 overflow-hidden rounded-full border border-champagne/40 sm:bottom-6 sm:left-6 sm:h-32 sm:w-32">
            <AnimatePresence initial={false}>
              <motion.img key={s.id} src={s.detail} alt={`${s.name} swatch`} initial={{ opacity: 0, scale: 1.2 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8, ease: EASE }} className="absolute inset-0 h-full w-full object-cover" />
            </AnimatePresence>
          </div>
          <span className="absolute right-4 top-4 z-20 font-mono text-xs uppercase tracking-[0.25em] text-cream/90 sm:right-6 sm:top-6">
            {String(idx + 1).padStart(2, "0")} / {String(SHADES.length).padStart(2, "0")}
          </span>
        </div>

        <div className="flex flex-col justify-center lg:col-span-6">
          <Eyebrow>03 — The Celestial Lab</Eyebrow>
          <h2 className="mt-5 font-display text-5xl font-light leading-[0.95] text-cream sm:text-6xl lg:text-7xl">
            <MaskText lines={["Find your", <em key="e" className="italic text-champagne">shade of light.</em>]} />
          </h2>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-stone sm:text-base">
              Tap a pan to see it worn. Move across the orb to tilt the light, the way pressed glitter shifts on the lid.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-col gap-8 sm:flex-row sm:items-center">
            <Orb shade={s} />
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-champagne">{s.finish}</p>
              <p data-testid="shade-lab-name" className="mt-2 font-display text-4xl text-cream sm:text-5xl">{s.name}</p>
              <p className="mt-3 text-cream">
                <span data-testid="shade-lab-price">{formatINR(price.price)}</span>
                {off > 0 && <span className="ml-3 text-sm text-stone line-through">{formatINR(price.compare)}</span>}
                {off > 0 && <span className="ml-3 font-mono text-xs text-champagne">−{off}%</span>}
                {live.available === false && <span className="ml-3 font-mono text-xs uppercase tracking-[0.16em] text-stone">Sold out</span>}
              </p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-4 gap-3 sm:grid-cols-8" role="radiogroup" aria-label="Celestial shades">
            {SHADES.map((sh, i) => (
              <button
                key={sh.id}
                role="radio"
                aria-checked={i === idx}
                aria-label={sh.name}
                data-testid={`shade-swatch-${sh.id}`}
                onClick={() => setIdx(i)}
                className="group flex flex-col items-center gap-2"
              >
                <span
                  className={`swatch-dot h-12 w-12 rounded-full transition-transform duration-500 group-hover:scale-110 ${i === idx ? "ring-2 ring-champagne ring-offset-4 ring-offset-ink-2" : ""}`}
                  style={{ background: `radial-gradient(circle at 32% 28%, #fff8ec 0%, ${sh.colors[0]} 22%, ${sh.colors[1]} 75%)` }}
                />
                <span className={`text-center text-[11px] leading-tight ${i === idx ? "text-champagne" : "text-stone"}`}>{sh.name}</span>
              </button>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <AddToBag p={live} testId="shade-lab-add-to-bag" className="btn-gold">Add {s.name} to bag</AddToBag>
            <a href={productUrl(s.handle)} data-testid="shade-lab-view-shade" className="btn-ghost">View shade</a>
            <a href={collectionUrl("celestial-pressed-pigment")} data-testid="shade-lab-all-shades" className="nav-link inline-flex items-center gap-1 self-center text-sm text-cream">
              All Celestial shades <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <ShadeFinder />
      </div>
    </section>
  );
};
